import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { loadOptions } from '@easy-lib/modules/order';
import {
	AddCustomerBankAccountResponseModel,
	BankItem,
	CustomerBankAccount,
	Payment,
	PaymentDay,
	PaymentHistoryItem,
	PaymentOptions,
	PaymentResponse,
	SettlementType
} from '../models/payment.model';
import { BaseService } from '../../shared/services';
import { BourseKalaInfo, MofidCartInfo } from '@easy-lib/constants/payment.constants';
import { DateHelper } from '@easy-lib/tools';

@Injectable({
	providedIn: 'root'
})
export class PaymentService extends BaseService {
	getPaymentOptions(): Observable<PaymentOptions> {
		return this.httpService.get<PaymentOptions>(this.mtsPath + this.apiUrls.easy + 'payment/payment-options').pipe(
			map(paymentOptions => this.setPaymentDaysLabels(paymentOptions)),
			map(paymentOptions => this.setFormattedDates(paymentOptions)),
			map(paymentOptions => this.setIsT0Valid(paymentOptions)),
			map(paymentOptions => this.filterPaymentDays(paymentOptions)),
			map(paymentOptions => this.sortPaymentDays(paymentOptions)),
			map(paymentOptions => this.addWalletToPaymentOptions(paymentOptions)),
			map(paymentOptions => this.filterBankAccounts(paymentOptions)),
			map(paymentOptions => this.addBourseKalaAccount(paymentOptions))
		) as Observable<PaymentOptions>;
	}
	private setPaymentDaysLabels(paymentOptions: PaymentOptions): PaymentOptions {
		paymentOptions.paymentDays.map(pd => {
			pd.value = pd.value > 0 ? pd.value : 0;
			pd.text =
				pd.settlementType === SettlementType.settlementT0 ? 'امروز' : DateHelper.getJDateLongWithDay(pd.date);
		});
		return paymentOptions;
	}
	private setFormattedDates = (paymentOptions: PaymentOptions) => {
		const currentTime = new Date().toLocaleTimeString('en-GB', {
			hour: '2-digit',
			minute: '2-digit'
		});
		const first = paymentOptions.paymentDays.find(i => i.settlementType === SettlementType.settlementT1);
		const firstSub = first?.text?.substring(0, first.text.indexOf(' -'));
		const secondSub = first?.text?.substring(first.text.indexOf('- ') + 2);
		const nextDay = `<b> ${firstSub} (${secondSub})</b>`;

		const hamiEndTime = new Date(paymentOptions?.hamiOptions?.hamiEndTime).toLocaleTimeString('en-GB', {
			hour: '2-digit',
			minute: '2-digit'
		});

		const hamiExtraEndTime = new Date(paymentOptions?.hamiOptions?.hamiExtraEndTime).toLocaleTimeString('en-GB', {
			hour: '2-digit',
			minute: '2-digit'
		});
		const paymentOptionsRes = paymentOptions?.hamiOptions?.hamiEndTime;
		const hamiEndTimeMinus15 = new Date(
			new Date(paymentOptionsRes).setMinutes(new Date(paymentOptionsRes).getMinutes() - 15)
		).toLocaleTimeString('en-GB', {
			hour: '2-digit',
			minute: '2-digit'
		});
		const hamiExtraEndTimeMinus15 = new Date(
			new Date(paymentOptions?.hamiOptions?.hamiExtraEndTime).setMinutes(
				new Date(paymentOptions?.hamiOptions?.hamiExtraEndTime).getMinutes() - 15
			)
		).toLocaleTimeString('en-GB', {
			hour: '2-digit',
			minute: '2-digit'
		});
		const nonHamiEndTimeMinus15 = new Date(
			new Date(paymentOptions?.hamiOptions?.nonHamiEndTime).setMinutes(
				new Date(paymentOptions?.hamiOptions?.nonHamiEndTime).getMinutes() - 15
			)
		).toLocaleTimeString('en-GB', {
			hour: '2-digit',
			minute: '2-digit'
		});
		paymentOptions.hamiOptions.currentTimeFormatted = currentTime;
		paymentOptions.hamiOptions.nextDayFormatted = nextDay;
		paymentOptions.hamiOptions.hamiEndTimeFormatted = hamiEndTime;
		paymentOptions.hamiOptions.hamiExtraEndTimeFormatted = hamiExtraEndTime;
		paymentOptions.hamiOptions.hamiEndTimeMinus15Formatted = hamiEndTimeMinus15;
		paymentOptions.hamiOptions.hamiExtraEndTimeMinus15Formatted = hamiExtraEndTimeMinus15;
		paymentOptions.hamiOptions.nonHamiEndTimeMinus15Formatted = nonHamiEndTimeMinus15;
		return paymentOptions;
	};

	private setIsT0Valid(paymentOptions: PaymentOptions): PaymentOptions {
		paymentOptions.bankAccounts.map(ba => {
			ba.isT0Valid = checkValidBanksForT0(ba.shebaNumber);
		});
		return paymentOptions;

		// https://salambank.net/blog/which-bank-is-this-number/
		function checkValidBanksForT0(shebaNumber: string) {
			return ['017', '012', '015', '018', '057', '019'].includes((shebaNumber ?? '-').slice(4, 7));
		}
	}

	private filterPaymentDays(paymentOptions: PaymentOptions): PaymentOptions {
		paymentOptions.paymentDays = paymentOptions.paymentDays.filter(
			i => ![SettlementType.settlementT4].includes(i.settlementType)
		);

		if (paymentOptions.todayIsHoliday) {
			paymentOptions.paymentDays = paymentOptions.paymentDays.filter(
				pd => ![SettlementType.settlementT0].includes(pd.settlementType)
			);
		}

		return paymentOptions;
	}

	private sortPaymentDays(paymentOptions: PaymentOptions): PaymentOptions {
		paymentOptions.paymentDays = paymentOptions.paymentDays.sort((a, b) => a.settlementType - b.settlementType);
		return paymentOptions;
	}
	private addWalletToPaymentOptions(paymentOptions: PaymentOptions): PaymentOptions {
		paymentOptions.bankAccounts.push(MofidCartInfo);
		const walletDeposit: PaymentDay = {
			date: 'امروز(آنی)',
			value: null,
			settlementType: SettlementType.settlement_Wallet,
			text: 'آنی _'
		};
		paymentOptions.paymentDays.unshift(walletDeposit);
		return paymentOptions;
	}
	private filterBankAccounts(paymentOptions: PaymentOptions): PaymentOptions {
		return { ...paymentOptions, bankAccounts: paymentOptions.bankAccounts.filter(a => Boolean(a.isActive)) };
	}
	private addBourseKalaAccount(paymentOptions: PaymentOptions): PaymentOptions {
		if (paymentOptions.hasImeWallet)
			return {
				...paymentOptions,
				bankAccounts: [...paymentOptions.bankAccounts, BourseKalaInfo]
			};

		return paymentOptions;
	}
	//#endregion paymentOptions

	getPayments(opt: loadOptions): Observable<PaymentHistoryItem[]> {
		return this.httpService.post<PaymentHistoryItem[]>(this.mtsPath + this.apiUrls.easy + 'payment/payments', {
			body: opt,
			guestUserRestriction: true
		});
	}

	cancelPayment(id: number): Observable<any> {
		return this.httpService.post<any>(`${this.mtsPath + this.apiUrls.easy}payment/cancel-payment`, {
			body: { id },
			guestUserRestriction: true
		});
	}

	getBanks(): Observable<BankItem[]> {
		return this.httpService.getCached<BankItem[]>('Banks', `${this.mtsPath + this.apiUrls.easy}payment/all-banks`, {
			guestUserRestriction: true
		});
	}

	postPayment(model: Payment): Observable<PaymentResponse> {
		return this.httpService.post<PaymentResponse>(this.mtsPath + this.apiUrls.easy + 'payment/payment', {
			body: model,
			guestUserRestriction: true
		});
	}
	postBourseKala(value: number): Observable<PaymentResponse> {
		return this.httpService.post<PaymentResponse>(this.mtsPath + this.apiUrls.easy + 'payment/payment-to-ime', {
			body: { value },
			guestUserRestriction: true
		});
	}
	addBankAccount(cba: CustomerBankAccount): Observable<AddCustomerBankAccountResponseModel> {
		return this.httpService.post<AddCustomerBankAccountResponseModel>(
			this.mtsPath + this.apiUrls.easy + 'payment/bank-account',
			{
				body: cba,
				guestUserRestriction: true
			}
		);
	}
}
